import apiService from './api';
import { API_BASE_URL } from '../utils/env';

// Gestion des erreurs Football Data avec messages bilingues
const handleFootballDataError = (error, language = 'fr') => {
    if (error.response?.status === 429) {
        return {
            message: language === 'fr' 
                ? 'Les scores sont temporairement indisponibles. Réessaie dans quelques minutes ! ⚽'
                : 'Scores are temporarily unavailable. Try again in a few minutes! ⚽',
            type: 'warning',
            showRetry: true
        };
    }
    
    if (error.response?.status === 403) {
        return {
            message: language === 'fr'
                ? 'Quota Football Data dépassé. Les scores seront mis à jour bientôt ! 🏆'
                : 'Football Data quota exceeded. Scores will be updated soon! 🏆',
            type: 'info',
            showRetry: false
        };
    }
    
    if (error.code === 'NETWORK_ERROR') {
        return {
            message: language === 'fr'
                ? 'Problème de connexion. Vérifie ta connexion internet ! 🌐'
                : 'Connection problem. Check your internet connection! 🌐',
            type: 'error',
            showRetry: true
        };
    }
    
    return {
        message: language === 'fr'
            ? 'Erreur inattendue. Réessaie plus tard ! 🔄'
            : 'Unexpected error. Try again later! 🔄',
        type: 'error',
        showRetry: true
    };
};

export const fetchLiveMatches = async (language = 'fr') => {
    try {
        const response = await fetch(`${API_BASE_URL}/matches/live`);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
            return data.matches;
        } else {
            throw new Error(data.message || 'Erreur lors du chargement des matchs');
        }
    } catch (error) {
        const errorInfo = handleFootballDataError(error, language);
        throw new Error(errorInfo.message);
    }
};

export const fetchMatchDetails = async (matchId, language = 'fr') => {
    try {
        const response = await fetch(`${API_BASE_URL}/matches/${matchId}`);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
            return data.match;
        } else {
            throw new Error(data.message || 'Erreur lors du chargement du match');
        }
    } catch (error) {
        const errorInfo = handleFootballDataError(error, language);
        throw new Error(errorInfo.message);
    }
};

export const submitPrediction = async (matchId, prediction) => {
    try {
        const response = await apiService.post(`/matches/${matchId}/predict`, { prediction });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Erreur lors de la soumission de la prédiction');
    }
};

class MatchService {
    async getMatches() {
        try {
            console.log('🔍 MatchService: Récupération des matchs depuis l\'API...');
            const response = await apiService.get('/matches');
            console.log('📡 MatchService: Réponse API reçue:', response);
            
            // Gérer la nouvelle structure de réponse du backend
            if (response && response.success && response.matches) {
                console.log(`✅ MatchService: ${response.matches.length} matchs trouvés`);
                return response.matches;
            } else if (response && response.matches && !response.success) {
                // Pas de matchs disponibles mais pas d'erreur
                console.log('ℹ️ MatchService: Aucun match disponible actuellement');
                return [];
            } else if (response && Array.isArray(response)) {
                // Ancien format (compatibilité)
                console.log(`✅ MatchService: ${response.length} matchs trouvés (format tableau)`);
                return response;
            } else {
                console.warn('⚠️ MatchService: Format de réponse inattendu:', response);
                return [];
            }
        } catch (error) {
            console.error('❌ MatchService: Erreur lors de la récupération des matchs:', error);
            throw error;
        }
    }

    async getMatchesWithCache() {
        console.log('🔄 MatchService: Vérification du cache...');
        
        // Vérifier le cache local
        const cached = localStorage.getItem('matches_cache');
        const cacheTime = localStorage.getItem('matches_cache_time');
        
        if (cached && cacheTime) {
            const now = Date.now();
            const cacheAge = now - parseInt(cacheTime);
            const maxAge = 5 * 60 * 1000; // 5 minutes
            
            if (cacheAge < maxAge) {
                console.log('💾 MatchService: Utilisation du cache (âge:', Math.round(cacheAge/1000), 's)');
                return JSON.parse(cached);
            } else {
                console.log('⏰ MatchService: Cache expiré, récupération fraîche...');
            }
        }
        
        // Récupérer les données fraîches
        try {
            console.log('🌐 MatchService: Appel API pour données fraîches...');
            const matches = await this.getMatches();
            
            // Mettre en cache
            localStorage.setItem('matches_cache', JSON.stringify(matches));
            localStorage.setItem('matches_cache_time', Date.now().toString());
            console.log('💾 MatchService: Données mises en cache');
            
            return matches;
        } catch (error) {
            console.error('❌ MatchService: Erreur lors de la récupération fraîche:', error);
            
            // En cas d'erreur, retourner le cache si disponible
            if (cached) {
                console.warn('🔄 MatchService: Utilisation du cache en raison d\'une erreur réseau');
                return JSON.parse(cached);
            }
            throw error;
        }
    }

    clearCache() {
        console.log('🗑️ MatchService: Cache vidé');
        localStorage.removeItem('matches_cache');
        localStorage.removeItem('matches_cache_time');
    }
}

export default new MatchService(); 